import React, { useState, useRef, createContext, createElement, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { v4 as uuid } from 'uuid';
import { Prompt, Alert, Confirm } from 'components/mui';
export const ModalContext = createContext();

/**
 * This is a helper context used to reduce the amount of DOM elements used on a page.  Instead of using DOM
 * elements it uses Promises.
 *
 * Example:
 *
 * const { prompt } = useContext(ModalContextProvider);
 *
 * prompt({
 *   "title": "What's your name?"
 * }).then(name => console.log(`Hellow, ${name}`));
 *
 * This context can be called as many times as necessary and stacks modals on top of each other. One caveat
 * I actually haven't tested multiple Modals and it's possible the Modal backdrops don't end up in the right
 * place and may need fixed if we actually need to stack modals.
 */
export const ModalContextProvider = ({
  children
}) => {
  const [renderIteration, setRenderIteration] = useState(0);
  const modalStack = useRef([]);
  const location = useLocation();
  useEffect(() => {
    return () => {
      // We had a route change so clear out all the modals.
      closeAll('Closed due to navigation');
    };
  }, [location]);

  /**
   * Function to handle either reject or resolve.
   *
   * @param {string} key               UUID for modal stack
   * @param {any} response             Response from the modal
   * @param {'reject'|'resolve'} func  Name of function to run
   */
  const _on = (key, response, func) => {
    const modal = modalStack.current.find(i => i.props.key === key);
    if (modal) {
      modal[func](response);
    }
    modalStack.current = modalStack.current.filter(i => i.props.key !== key);
    setRenderIteration(i => i + 1);
  };

  /**
   * Reject and clear every modal currently on the stack. Used when the surrounding
   * context is torn down out from under open modals — a route change, or a Customer
   * Journeys revision reload that destroys and rebuilds the Pixi node containers the
   * open modal is positioned against. Leaving the modal open in that case renders it
   * against a destroyed container and crashes the app (CIN-6428).
   *
   * @param {string} reason  Rejection reason passed to each modal's promise
   */
  const closeAll = (reason = 'Closed') => {
    modalStack.current.forEach(modal => {
      _on(modal.props.key, reason, 'reject');
    });
  };

  /**
   * When the modal is closed
   *
   * @param {string} key    UUID for modal stack
   * @param {any} response  Response from the modal
   */
  const onClose = (key, response) => {
    _on(key, response, 'reject');
  };

  /**
   * When the modal is submitted
   *
   * @param {string} key    UUID for modal stack
   * @param {any} response  Response from the modal
   */
  const onSubmit = (key, response) => {
    _on(key, response, 'resolve');
  };

  /**
   * Function to launch a modal
   *
   * @param {Componet} component    Modal component
   * @param {ComponentProps} props  Props to pass to the Modal
   *
   * @returns Promise
   */
  const show = (component, props) => {
    if (!props) props = {};
    return new Promise((resolve, reject) => {
      props.key = uuid();
      props.open = true;
      // TODO do we need to handle if there is an onClose/onSubmit prop passed in?
      props.onClose = response => onClose(props.key, response);
      props.onSubmit = response => onSubmit(props.key, response);
      modalStack.current.push({
        resolve,
        reject,
        props,
        component
      });
      setRenderIteration(i => i + 1);
    }); // DO NOT DO A CATCH HERE. It will cause the confirmation dialog to always return a confirm (e.g., Do you want to delete? Cancel... too bad we're going to delete it anyway)
  };

  /**
   * Helper function to open a <Prompt> modal
   *
   * @param {object} props any component props
   *
   * @returns Promise
   */
  const prompt = props => show(Prompt, props);

  /**
   * Helper function to open a <Alert> modal
   *
   * @param {object} props any component props
   *
   * @returns Promise
   */
  const alert = props => show(Alert, props);

  /**
   * Helper function to open a <Confirm> modal
   *
   * @param {object} props any component props
   *
   * @returns Promise
   */
  const confirm = props => show(Confirm, props);
  return <ModalContext.Provider value={{
    prompt,
    alert,
    confirm,
    show,
    closeAll
  }}>
      {children}
      {/*
        Each modal renders inside its own error boundary so that a render exception in
        one modal is reported to Sentry and quietly removes that modal, instead of
        propagating up and unmounting the entire app to a white screen. The app has no
        other true error boundary around this layer. See CIN-6428.
       */}
      {modalStack.current.map(stack => <Sentry.ErrorBoundary key={stack.props.key} fallback={() => null}>
          {createElement(stack.component, stack.props)}
        </Sentry.ErrorBoundary>)}
    </ModalContext.Provider>;
};